import {
  retargetRagScript,
  type PendingKnowledgeSetup,
} from './knowledge-keys';

export const RAG_SEARCH_COMMAND = 'GetRAGSearchResponse';
export const RAG_SCRIPT_PATH = `services/${RAG_SEARCH_COMMAND}.py`;

/** What the run needs from the app — injected, so the order is testable. */
export interface KnowledgeSetupDeps {
  /** Uploads one file into the draft version's `services/`. */
  uploadScript: (file: File) => Promise<void>;
  /** A draft file's text (throws when it isn't there). */
  readScript: (path: string) => Promise<string>;
  /** `POST /ai/knowledge/<route>` → its JSON body, or throws its `detail`. */
  post: <T>(route: 'corpus' | 'file', body: unknown) => Promise<T>;
  /**
   * Sets the search node's corpus ids and saves the row. Returns `false` when
   * the search node is gone (deleted while the run was going), in which case
   * nothing was written.
   */
  setCorpusId: (corpusId: string) => boolean;
  /** The search node is there to receive a corpus. */
  hasSearchNode: () => boolean;
  /** Reports a step to the user; the text is built from it, not here. */
  progress: (step: KnowledgeProgress) => void;
  /**
   * The solution the run started in is still the open one. Asked before the
   * steps that read or write the OPEN solution's rows: the search-node read and
   * the corpus id write (`assertSameSolution` in `runSteps`). `false` stops the
   * run before that step.
   */
  isSameSolution: () => boolean;
  /**
   * A DIFFERENT solution is known to be open (not merely none). Asked before
   * each script upload (`assertNoOtherSolution` in `runSteps`): an upload is
   * addressed by the ids the run started with, so leaving the solution does not
   * stop it, opening another one does.
   */
  isAnotherSolutionOpen: () => boolean;
}

/** One step of the run, as data: the wording lives in `knowledge-setup-text.ts`. */
export type KnowledgeProgress =
  | { step: 'adding-key'; name: string }
  | { step: 'creating-corpus'; project: string }
  | { step: 'adding-document'; name: string }
  | { step: 'pointing-search' };

export interface KnowledgeSetupResult {
  keysAdded: number;
  corpusId?: string;
  projectId?: string;
  documents: number;
  scriptRetargeted: boolean;
}

const toBase64 = async (file: File) => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
};

/** The search node vanished before the corpus id could be written onto it. */
class SearchNodeGoneError extends Error {
  constructor() {
    super('The search node was removed from the solution during the setup.');
    this.name = 'SearchNodeGoneError';
  }
}

/** Another solution was opened before a step that touches the open one. */
class SolutionChangedError extends Error {
  constructor() {
    super('The open solution changed during the setup.');
    this.name = 'SolutionChangedError';
  }
}

/** No solution is open any more (the user left; none other was opened). */
class SolutionLeftError extends Error {
  constructor() {
    super('The solution was left during the setup.');
    this.name = 'SolutionLeftError';
  }
}

/** Uploading one file into `services/` failed; `original` is what it threw. */
class ScriptUploadError extends Error {
  constructor(
    readonly fileName: string,
    readonly original: unknown
  ) {
    super(original instanceof Error ? original.message : String(original));
    this.name = 'ScriptUploadError';
  }
}

/** The steps of `runKnowledgeSetup`, recording progress in `result`. */
const runSteps = async (
  setup: PendingKnowledgeSetup,
  deps: KnowledgeSetupDeps,
  result: KnowledgeSetupResult
): Promise<KnowledgeSetupResult> => {
  // Before an upload: stop only when ANOTHER solution is open. The upload is
  // addressed by the ids the run started with, and its draft hand-over is
  // guarded separately (`onDone` in `knowledge-setup-deps.ts`), so merely
  // leaving the solution does not need to stop it.
  const assertNoOtherSolution = () => {
    if (deps.isAnotherSolutionOpen()) throw new SolutionChangedError();
  };
  // Before a step that reads or writes the OPEN solution's rows (the
  // search-node read, the corpus id write): the strict check. When it fails and
  // no other solution is open, the user left rather than switched.
  const assertSameSolution = () => {
    if (deps.isSameSolution()) return;
    throw deps.isAnotherSolutionOpen()
      ? new SolutionChangedError()
      : new SolutionLeftError();
  };
  const upload = async (file: File) => {
    assertNoOtherSolution();
    try {
      await deps.uploadScript(file);
    } catch (err) {
      throw new ScriptUploadError(file.name, err);
    }
  };

  for (const key of setup.keys) {
    deps.progress({ step: 'adding-key', name: key.name });
    // eslint-disable-next-line no-await-in-loop -- one upload at a time, as Actions does
    await upload(new File([key.text], key.name, { type: 'text/x-python' }));
    result.keysAdded += 1;
  }

  const key = setup.keys[setup.corpusKey] ?? setup.keys[0];
  if (!key || !setup.references.length) return result;
  assertSameSolution();
  if (!deps.hasSearchNode()) return result;

  deps.progress({ step: 'creating-corpus', project: key.projectId });
  const corpus = await deps.post<{
    corpus: string;
    corpusId: string;
    projectId: string;
  }>('corpus', {
    credentials: key.text,
    displayName: `${setup.title} knowledge`.slice(0, 120),
    description: `Created by Pro Studio from ${setup.references.length} reference(s).`,
  });
  result.corpusId = corpus.corpusId;
  result.projectId = corpus.projectId;

  for (const file of setup.references) {
    deps.progress({ step: 'adding-document', name: file.name });
    // eslint-disable-next-line no-await-in-loop -- one per request: the serverless time limit
    await deps.post('file', {
      credentials: key.text,
      corpus: corpus.corpus,
      file: {
        name: file.name,
        mimeType: file.type || 'text/plain',
        base64: await toBase64(file),
      },
    });
    result.documents += 1;
  }

  const script = await deps.readScript(RAG_SCRIPT_PATH);
  const retargeted = retargetRagScript(script, {
    projectId: corpus.projectId,
    keyFileName: key.name,
  });
  if (retargeted) {
    deps.progress({ step: 'pointing-search' });
    await upload(
      new File([retargeted], `${RAG_SEARCH_COMMAND}.py`, {
        type: 'text/x-python',
      })
    );
    result.scriptRetargeted = true;
  }

  // After the last await: `setCorpusId` writes onto whichever solution's rows
  // are loaded now.
  assertSameSolution();
  if (!deps.setCorpusId(corpus.corpusId)) throw new SearchNodeGoneError();
  return result;
};

/**
 * What a failed setup knows, as data: the user text is built from it by
 * `knowledge-setup-text.ts` out of translation keys, so nothing here is a
 * sentence meant for the screen.
 */
export interface KnowledgeSetupFailure {
  /**
   * `search-node-missing`: the corpus was built but could not be attached.
   * `solution-changed`: another solution was opened before a step that touches
   * the open one, and the run stopped there.
   * `solution-left`: no solution was open before a step that writes onto the
   * open solution's rows (the user left without opening another).
   */
  reason:
    | 'step-failed'
    | 'search-node-missing'
    | 'solution-changed'
    | 'solution-left';
  /**
   * The message of what threw, for logs. For a `step-failed` it is whatever
   * the failing step threw, which is not always server text: a `detail` from
   * `/ai/knowledge/*`, a transport error, the upload's own answer, or this
   * app's English read error from `fetchVersionFileContent`.
   */
  cause: string;
  /**
   * Set when the step that failed was uploading this file into `services/`:
   * the user text names the file from a translation key instead of showing
   * `cause`.
   */
  failedUpload?: string;
  keysAdded: number;
  keysTotal: number;
  corpusId?: string;
  projectId?: string;
  documents: number;
  documentsTotal: number;
}

const reasonOf = (err: unknown): KnowledgeSetupFailure['reason'] => {
  if (err instanceof SearchNodeGoneError) return 'search-node-missing';
  if (err instanceof SolutionChangedError) return 'solution-changed';
  if (err instanceof SolutionLeftError) return 'solution-left';
  return 'step-failed';
};

/**
 * A failed setup: what went wrong and what was left behind, plus the original
 * error. Nothing is undone. The keys already added stay in `services/`. A
 * corpus lives in the customer's own Google Cloud project, where deleting it
 * is theirs to decide, and `bobcat/src/server-only/knowledge/vertex-rag.ts` has no delete
 * operation at all. So the failure names the corpus and how far it got.
 * `message` is an English summary for logs; the UI does not show it.
 */
export class KnowledgeSetupError extends Error {
  constructor(
    readonly failure: KnowledgeSetupFailure,
    readonly original: unknown
  ) {
    super(
      `${failure.reason}: ${failure.cause} (keys ${failure.keysAdded}/${failure.keysTotal}` +
        `${failure.corpusId ? `, corpus ${failure.corpusId} in ${failure.projectId}, documents ${failure.documents}/${failure.documentsTotal}` : ''})`
    );
    this.name = 'KnowledgeSetupError';
  }
}

/**
 * After Talk it through creates the solution: keys into `services/`, then —
 * when references were attached — a corpus built from them in the chosen
 * key's project, the search script pointed at that project and key, and the
 * corpus id written onto the search node (which is what releases the first
 * auto-publish; see the `live_corpus_id` subscription in `table-store.ts`).
 *
 * On a failure it rejects with a `KnowledgeSetupError`: the structured
 * `failure` (what was left behind) and the original error.
 *
 * Opening another solution stops the run before its next upload or step that
 * touches the open solution (`assertNoOtherSolution`, `assertSameSolution` in
 * `runSteps`); leaving the solution stops it only before the search-node read
 * and the corpus id write. LIMITATION: what the run
 * already did under the ids it started with is not undone — keys uploaded into
 * the original solution stay there, and a corpus already created in the
 * customer's Google Cloud project stays unused; the failure names it.
 */
export const runKnowledgeSetup = async (
  setup: PendingKnowledgeSetup,
  deps: KnowledgeSetupDeps
): Promise<KnowledgeSetupResult> => {
  const result: KnowledgeSetupResult = {
    keysAdded: 0,
    documents: 0,
    scriptRetargeted: false,
  };
  try {
    return await runSteps(setup, deps, result);
  } catch (err) {
    throw new KnowledgeSetupError(
      {
        reason: reasonOf(err),
        cause: err instanceof Error ? err.message : String(err),
        ...(err instanceof ScriptUploadError && { failedUpload: err.fileName }),
        keysAdded: result.keysAdded,
        keysTotal: setup.keys.length,
        corpusId: result.corpusId,
        projectId: result.projectId,
        documents: result.documents,
        documentsTotal: setup.references.length,
      },
      err instanceof ScriptUploadError ? err.original : err
    );
  }
};

/**
 * The failure to report for whatever a run rejected with. A
 * `KnowledgeSetupError` carries its own; anything else (the run threw before
 * its try block, or something non-Error) is a `step-failed` with nothing done
 * recorded.
 */
export const failureOfError = (err: unknown): KnowledgeSetupFailure =>
  err instanceof KnowledgeSetupError
    ? err.failure
    : {
        reason: 'step-failed',
        cause: err instanceof Error ? err.message : String(err),
        keysAdded: 0,
        keysTotal: 0,
        documents: 0,
        documentsTotal: 0,
      };
